import { Box, Text } from '@modusign/design-system';
import PropTypes from 'prop-types';
import PropsType from 'props-type';
import { useMemo } from 'react';

import SignatureFieldMarkerIcon from '@app/components/SignatureFieldMarkerIcon';
import orderRotationForColor from '@app/libs/participantFields/orderRotationForColor';

const propTypes = {
  order: PropTypes.number.isRequired,
  iconSize: PropTypes.number,
  type: PropTypes.oneOf(['SIGN_OR_STAMP', 'SIGN', 'STAMP'] as const),
  onSelect: PropTypes.func,
};

const defaultProps = {
  order: 1,
  iconSize: 14,
  type: 'SIGN_OR_STAMP',
  onSelect(_type: string) {},
};

type SignatureTypeSelectListItemProps = PropsType<typeof propTypes, typeof defaultProps>;

function SignatureTypeSelectListItem({
  order,
  iconSize,
  type,
  onSelect,
}: SignatureTypeSelectListItemProps) {
  const iconType = useMemo(() => {
    switch (type) {
      case 'SIGN':
        return 'SIGN';
      case 'STAMP':
        return 'STAMP';
      case 'SIGN_OR_STAMP':
      default:
        return 'CIRCLE';
    }
  }, [type]);

  const signatureTypeText = useMemo(() => {
    switch (type) {
      case 'SIGN':
        return '사인';
      case 'STAMP':
        return '도장';
      case 'SIGN_OR_STAMP':
      default:
        return '사인·도장';
    }
  }, [type]);

  function handleSelect(value: string) {
    onSelect(value);
  }

  const colorByOrder = `participantOrder${orderRotationForColor(order)}`;

  return (
    <Box as="li" key={type} width="100%">
      <Box
        as="button"
        sx={{
          color: 'textNeutralVeryLight',
          height: 36,
          padding: '8px 16px',
          position: 'relative',
          textAlign: 'left',
          width: '100%',
          '&:hover': {
            backgroundColor: 'lightGray50',
            color: 'textNeutralLight',
          },
        }}
        type="button"
        onClick={() => handleSelect(type)}
      >
        <Box display="inline-block" height="20px" width="20px">
          <Box
            height={`${iconSize}px`}
            pl={`${(20 - iconSize) / 2}px`}
            pt={`${(20 - iconSize) / 2}px`}
            width={`${iconSize}px`}
          >
            <SignatureFieldMarkerIcon color={colorByOrder} size={iconSize} type={iconType} />
          </Box>
        </Box>
        <Text
          color="inherit"
          fontSize="md"
          fontWeight="medium"
          pl={2}
          sx={{ position: 'absolute', top: 3 }}
        >
          {signatureTypeText}
        </Text>
      </Box>
    </Box>
  );
}

SignatureTypeSelectListItem.propTypes = propTypes;
SignatureTypeSelectListItem.defaultProps = defaultProps;

export default SignatureTypeSelectListItem;
